// src/pages/Search.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box, VStack, HStack, FormControl, FormLabel,
  Input, IconButton, Button, Select
} from '@chakra-ui/react';
import { SearchIcon, ChatIcon } from '@chakra-ui/icons';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { flushSync } from 'react-dom';
import Header from '../components/Header';
import { initiateSearch, tell, getLoads, login, conversationStarted } from '../api/auth';
import { useLoads } from '../context/LoadsContext';
import { useAuth } from '../context/AuthContext';

export default function Search() {
  const nav      = useNavigate();
  const driverId = localStorage.getItem('driverId');
  const {loads, setLoads} = useLoads();
  const { setToken, token } = useAuth();
  const [loginReady, setLoginReady] = useState(!!token);

  // Auto-login effect
  useEffect(() => {
    const doLogin = async () => {
      if (token) {
        setLoginReady(true);
        return;
      }
      try {
        const { driver_id, session_id } = await login('test_user', 'test123');
        setToken(localStorage.getItem('token'));
        await conversationStarted(driver_id);
        setLoginReady(true);
      } catch (err) {
        // Optionally handle error (show toast, etc.)
        setLoginReady(false);
      }
    };
    doLogin();
    // eslint-disable-next-line
  }, []);

  // ✅ Grab the shared refs from AppLayout
  const {
    originRef,
    destinationRef,
    dateRef,
    equipmentRef
  } = useOutletContext();

  // Local UI state
  const [origin, setOrigin]           = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate]               = useState('');
  const [equipment, setEquipment]     = useState('flatbed');
  const [highlightField, setHighlightField] = useState(null);
  const [isConversationStarted, setIsConversationStarted] = useState(false);

  // Glow logic
  useEffect(() => {
    if (!highlightField) return;
    const t = setTimeout(() => setHighlightField(null), 800);
    return () => clearTimeout(t);
  }, [highlightField]);
  
  // keep checking if isSearching, and when it is true, show a loading spinner, 
  // once it becomes true never check again
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    let interval;
    const checkSearching = () => {
      const searching = localStorage.getItem('isSearching') === 'true';
      if (searching) {
        setIsSearching(true);
        // Clear interval once we find searching is true
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };

    // Initial check
    checkSearching();
    
    // Only set up interval if not already searching
    if (!isSearching) {
      interval = setInterval(checkSearching, 100); // Check more frequently
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isSearching]); // Only re-run if isSearching changes

  // Sync ref values with state
  useEffect(() => {
    if (!loginReady) return;
    const syncRefsWithState = () => {
      if (originRef.current?.value !== origin) {
        setOrigin(originRef.current?.value || '');
      }
      if (destinationRef.current?.value !== destination) {
        setDestination(destinationRef.current?.value || '');
      }
      if (dateRef.current?.value !== date) {
        setDate(dateRef.current?.value || '');
      }
      if (equipmentRef.current?.value !== equipment) {
        setEquipment(equipmentRef.current?.value || '');
      }
    };

    // Set up an interval to check for changes
    const interval = setInterval(syncRefsWithState, 100);
    
    // Initial sync
    syncRefsWithState();

    return () => clearInterval(interval);
  }, [origin, destination, date, equipment, loginReady]);

  // ⭐ ADD THIS useEffect TO LOG VALUES WHEN ALL FIELDS ARE FILLED ⭐
  useEffect(() => {
    const fetchAndTellData = async () => {
      if (!origin || !destination || !date || !equipment) return null;

      let dt = date;
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dt)) {
        const [m,day,y] = dt.split('/');
        dt = `${y}-${m.padStart(2,'0')}-${day.padStart(2,'0')}`;
      }

      console.log("All input fields now have values:");
      console.log("Origin:", origin);
      console.log("Destination:", destination);
      console.log("Date:", dt);
      console.log("Equipment:", equipment);

      await tell(driverId, 'origin',      origin);
      await tell(driverId, 'destination', destination);
      await tell(driverId, 'date',        dt);
      await tell(driverId, 'equipment',   equipment);
      await initiateSearch(driverId);
    };
    
    fetchAndTellData();
  }, [origin, destination, date, equipment, driverId]);

  // Start conversation and trigger widget
  useEffect(() => {
    if (!loginReady) return;
    const initiateCall = async () => {
      const MAX_RETRIES = 10;
      const RETRY_INTERVAL = 500; // milliseconds
      let retries = 0;
      const waitForElement = (selector, withinShadowRoot = false) => {
        return new Promise((resolve) => {
          const interval = setInterval(() => {
            const widget = document.querySelector('elevenlabs-convai');
            if (!widget) return;
            let element;
            if (withinShadowRoot && widget.shadowRoot) {
              element = widget.shadowRoot.querySelector(selector);
            } else {
              element = widget.querySelector(selector);
            }
            if (element) {
              clearInterval(interval);
              resolve(element);
            } else if (retries >= MAX_RETRIES) {
              clearInterval(interval);
              resolve(null);
            }
            retries++;
          }, RETRY_INTERVAL);
        });
      };
      // add a delay before we check for
      await new Promise((res) => setTimeout(res, 1000));
      // Step 1: Wait for the widget to load
      const widget = document.querySelector('elevenlabs-convai');
      if (!widget) {
        console.error('ElevenLabs widget not found.');
        return;
      }
      const storedDriverId = localStorage.getItem('driverId');
      const storedSessionId = localStorage.getItem('sessionId');
      widget.setAttribute('dynamic-variables', JSON.stringify({ driver_id: storedDriverId, session_id: storedSessionId, context:"" }));
      // Step 2: If the widget is collapsed, click to expand
      const isExpanded = widget.classList.contains('_open_1968y_57');
      if (!isExpanded) {
        const avatarButton = await waitForElement('button._avatarButton_1968y_166', true);
        if (avatarButton) {
          avatarButton.click();
          await new Promise((res) => setTimeout(res, 1000)); // Wait for expansion
        } else {
          console.error('Avatar button not found.');
          return;
        }
      }
      // Step 3: Wait for the "Click Me" button and initiate the call
      const callButton = await waitForElement('button[title="Click Me"]', true);
      if (callButton && !callButton.disabled) {
        callButton.click();
      } else {
        console.error('"Click Me" button not found or disabled.');
      }
    };
    initiateCall();
  }, [loginReady]);
  

  // Set initial equipment value in ref
  useEffect(() => {
    if (!loginReady) return;
    if (equipmentRef.current) {
      equipmentRef.current.value = 'flatbed';
    }
  }, [equipmentRef, loginReady]);

  // Fetch & tell backend
  const fetchAndTellData = useCallback(async () => {
    const o  = originRef.current.value;
    const d  = destinationRef.current.value;
    let dt  = dateRef.current.value;
    const eq = equipmentRef.current.value;
    if (!o||!d||!dt||!eq) return null;

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dt)) {
      const [m,day,y] = dt.split('/');
      dt = `${y}-${m.padStart(2,'0')}-${day.padStart(2,'0')}`;
    }

    await tell(driverId, 'origin',      o);
    await tell(driverId, 'destination', d);
    await tell(driverId, 'date',        dt);
    await tell(driverId, 'equipment',   eq);
    const { data } = await getLoads(driverId);

    
// Update your API functions to fetch load data in the appropriate format, including:

// ```jsx
// const sampleLoad = {
//   id: "123",
//   carrier: "AMX LOGISTICS",
//   origin: "JFK Airport, NY",
//   destination: "Walmart, Jersey, 249",
//   tripMiles: 800,
//   equipment: "Dry Van",
//   rateUsd: 2.95,
//   estimatedRatePerMile: 2.95,
//   pickupEarliest: "2025-05-07T09:00:00Z",
//   pickupLatest: "2025-05-07T12:00:00Z",
//   originDeadheadMiles: 120,
//   maxWeightLbs: 45100,
//   topInfo: {
//     overall: {
//       score: 85
//     },
//     market_rate_diff: 12, // Positive for above market, negative for below
//     overall_summary: "Good rate, reasonable distance"
//   }
// };
// ```


// data.map is not a function
// TypeError: data.map is not a function


    const loadData = data.loads.map(load => ({
      id: load.id,
      carrier: load.carrier,
      origin: load.origin,
      destination: load.destination,
      tripMiles: load.tripMiles,
      equipment: load.equipment,
      rateUsd: load.rateUsd,
      estimatedRatePerMile: load.estimatedRatePerMile,
      pickupEarliest: load.pickupEarliest,
      pickupLatest: load.pickupLatest,
      originDeadheadMiles: load.originDeadheadMiles,
      maxWeightLbs: load.maxWeightLbs,
      topInfo: load.topInfo
    }));

    return { loads: loadData };
  }, [driverId, originRef, destinationRef, dateRef, equipmentRef]);
    

  const navigateWithData = useCallback(data => {
    if (!data?.loads?.length) {
      alert('No loads found. Please adjust your search.');
      return;
    }
    localStorage.setItem('loads_data', JSON.stringify(data));
    setLoads(data.loads)
    nav('/results');
  }, [nav]);

  const handleSearch = useCallback(async () => {
    console.log('handleSearch on search page');
    const data = await fetchAndTellData();
    navigateWithData(data);
  }, [fetchAndTellData, navigateWithData]);

  const isFormValid = Boolean(origin && destination && date && equipment);

  if (!loginReady) {
    return <Box p={8} textAlign="center">Logging in...</Box>;
  }

  return (
    <>
      <Header title="Search Loads" />
      <Box px={4}>
        <VStack spacing={6} align="stretch">
          {/* Origin */}
          <FormControl id="origin" isRequired>
            <FormLabel>Origin</FormLabel>
            <Input
              ref={originRef}
              placeholder="City, State"
              value={origin}
              onChange={e => { setOrigin(e.target.value); }}
              boxShadow={highlightField==='origin'
                ? '0 0 0 3px rgba(66,153,225,0.6)'
                : undefined
              }
              transition="box-shadow 0.3s ease"
            />
          </FormControl>

          {/* Destination */}
          <FormControl id="destination" isRequired>
            <FormLabel>Destination</FormLabel>
            <Input
              ref={destinationRef}
              placeholder="City, State"
              value={destination}
              onChange={e => { setDestination(e.target.value); }}
              boxShadow={highlightField==='destination'
                ? '0 0 0 3px rgba(66,153,225,0.6)'
                : undefined
              }
              transition="box-shadow 0.3s ease"
            />
          </FormControl>

          {/* Date */}
          <FormControl id="date" isRequired>
            <FormLabel>Travel Date</FormLabel>
            <Input
              type="date"
              ref={dateRef}
              value={date}
              onChange={e => { setDate(e.target.value); }}
              boxShadow={highlightField==='date'
                ? '0 0 0 3px rgba(66,153,225,0.6)'
                : undefined
              }
              transition="box-shadow 0.3s ease"
            />
          </FormControl>

          {/* Equipment */}
          <FormControl id="equipment" isRequired>
            <FormLabel>Equipment</FormLabel>
            <Select
              ref={equipmentRef}
              placeholder="Select equipment"
              value={equipment}
              onChange={e => { setEquipment(e.target.value); }}
              boxShadow={highlightField==='equipment'
                ? '0 0 0 3px rgba(66,153,225,0.6)'
                : undefined
              }
              transition="box-shadow 0.3s ease"
            >
              <option value="dry van">Dry Van</option>
              <option value="reefer">Reefer</option>
              <option value="flatbed">Flatbed</option>
              <option value="conestoga">Conestoga</option>
              <option value="step deck">Step Deck</option>
            </Select>
          </FormControl>

          <HStack spacing={4} pt={2}>
            <Button
              leftIcon={<SearchIcon />}
              colorScheme="brand"
              bg="#FD984F"
              color="#202020"
              fontSize="20px"
              fontWeight="500"
              lineHeight="24px"
              letterSpacing="1px"
              mb="16px"
              _hover={{ bg: '#FFE8CC' }}
              _active={{ bg: '#FFD6AA' }}
              flex="1"
              size="lg"
              onClick={handleSearch}
              isDisabled={!isFormValid || isSearching}
              isLoading={isSearching}
              loadingText="Searching..."
            >
              Search
            </Button>
          </HStack>
        </VStack>
      </Box>
    </>
  );
}
